import { useRef, useState } from "react";
import { Upload, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API } from "../lib/api";

const CHUNK = 1_200_000; // base64 chars per request (~0.9 MB binary) — stays well under proxy body limits

export const mediaSrc = (url) => (url && url.startsWith("/api/") ? `${API}${url.replace(/^\/api/, "")}` : url);

export async function uploadMedia(file, { alt = "", onProgress } = {}) {
  if (file.size > 10 * 1024 * 1024) throw new Error("File exceeds the 10 MB limit");
  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const b64 = dataUrl.split(",")[1];
  const total = Math.ceil(b64.length / CHUNK);
  const upload_id = crypto.randomUUID();
  let result = null;
  for (let index = 0; index < total; index++) {
    const { data } = await api.post("/admin/media/chunk", { upload_id, index, total, data: b64.slice(index * CHUNK, (index + 1) * CHUNK), name: file.name, mime: file.type, alt });
    onProgress?.(Math.round(((index + 1) / total) * 100));
    if (data.complete) result = data;
  }
  return result;
}

export const MediaPicker = ({ value, onChange, accept = "image/*", label = "Upload", testid = "media-upload" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setPct(0);
    try { const m = await uploadMedia(f, { onProgress: setPct }); onChange(m.url); toast.success("Uploaded to media library"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      {value && accept.startsWith("image") && <img src={mediaSrc(value)} alt="" className="w-10 h-10 rounded object-cover border border-black/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#0d1724] text-[#0d1724] text-xs font-bold hover:bg-[#0f4a32] hover:text-[#d6aa43] disabled:opacity-50" data-testid={testid}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : accept.startsWith("image") ? <ImageIcon size={12} /> : <Upload size={12} />} {busy ? `${pct}%` : label}
      </button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
    </div>
  );
};
